import { getResultHighJumpHeights } from 'config/admin/result';
import React from 'react';
import { convertDateTime, convertMillisecondsToRecord } from 'utils/time';
import { redColor, whiteColor } from 'config/global';
import IconTextButton from 'components/common/IconTextButton';
import { ReactComponent as EyeIcon } from 'assets/images/icon_eye.svg';
import { getEvents } from 'api/eventApi';
import { isTeamResult } from 'utils/relayTeams';
import routes from 'routes';
import {
  updateSelectedAgeResult,
  updateSelectedEvent,
} from './Event/eventSlice';

/**
 * Transforms an array of results into a format suitable for an event track table.
 *
 * @param {Array} results - The array of result objects to be transformed.
 * @returns {Array} data - The transformed array of objects, each representing a row in the event track table.
 */
export const resultsToEventTrackTableData = (results) => {
  const data = [];
  results.forEach((result) => {
    data.push({
      ...result,
      // A relay team has no bib; anyone else without one is a walk-up guest.
      athleteNumber:
        result.athleteNumber || (isTeamResult(result) ? '' : 'Guest'),
      athleteAge: result.athleteAge && result.athleteAge > 0 ? result.athleteAge : '',
      result: result.status === 'OK' ? result.resultValue : '',
      date: `${convertDateTime(result.timestamp || '').date} ${convertDateTime(result.timestamp || '').time}`,
      athleteName: `${result.athleteFirstName} ${result.athleteLastName}`,
      position: result.status === 'OK' ? result.position : '',
      pr: result.isPR ? 'YES' : 'NO',
      seasonBestValue: !result.seasonBestValue
        ? ''
        : result.unit === 's'
          ? convertMillisecondsToRecord(result.seasonBestValue, 2)
          : Number(result.seasonBestValue).toFixed(2),
      attempt1:
        result.attempts?.length > 0
          ? result.attempts[0]
          : { distance: result.resultValue, status: 'OK' },
      attempt2: result.attempts?.length > 1 ? result.attempts[1] : null,
      attempt3: result.attempts?.length > 2 ? result.attempts[2] : null,
      attempt4: result.attempts?.length > 3 ? result.attempts[3] : null,
      attempt5: result.attempts?.length > 4 ? result.attempts[4] : null,
      attempt6: result.attempts?.length > 5 ? result.attempts[5] : null,
      attempts: result?.attempts?.map((attempt, index) => (
        <div
          style={{ paddingLeft: 15, minWidth: 70, paddingRight: 10 }}
          key={index}
        >
          {attempt.status === 'OK'
            ? `${attempt.distance.toFixed(2)}`
            : attempt.status === 'Foul'
              ? 'X'
              : attempt.status}
        </div>
      )),
    });
  });

  return data;
};

/**
 * Transforms an array of track event result objects into a format suitable for displaying
 * in an age view event track table, including athlete details, results, and action buttons.
 *
 * @param {Array} results - The list of result objects to transform.
 * @param {string|number} clubId - The identifier for the club.
 * @param {string|number} meetId - The identifier for the meet.
 * @param {string} eventType - The type of event (e.g., track event).
 * @param {string} roundType - The type of round (e.g., 'Final', 'Heat', 'Semi')
 * @param {NavigateFunction} navigate - Navigation function to handle routing to event views.
 * @param {DispatchType} dispatch - Dispatch function for state management or side effects.
 * @returns {Array} An array of transformed result objects formatted for table display.
 */
export const resultsToAgeViewEventTrackTableData = (
  results,
  clubId,
  meetId,
  eventType,
  roundType = null,
  navigate,
  dispatch
) => {
  const data = [];
  results.forEach((result) => {
    data.push({
      ...result,
      result: result.status === 'OK' ? result.resultValue : '',
      date: `${convertDateTime(result.timestamp || '').date} ${convertDateTime(result.timestamp || '').time}`,
      athleteName: `${result.athleteFirstName} ${result.athleteLastName}`,
      position: result.status === 'OK' ? result.position : '',
      pr: result.isPR ? 'YES' : 'NO',
      // Same form as the event view and high jump: two decimals, no unit.
      seasonBestValue: !result.seasonBestValue
        ? ''
        : result.unit === 's'
          ? convertMillisecondsToRecord(result.seasonBestValue, 2)
          : Number(result.seasonBestValue).toFixed(2),
      button: (
        <IconTextButton
          text=""
          icon={<EyeIcon />}
          onClick={() =>
            navigateToEventViewFromAgeView(
              result,
              clubId,
              meetId,
              eventType,
              navigate
            )
          }
          style={{ marginRight: 10 }}
          textColor={redColor}
          borderColor={whiteColor}
          height="unset"
          iconPadding="5px"
          iconColor={redColor}
          iconPosition="right"
        />
      ),
    });
  });

  return data;
};

export const navigateToEventView = async (
  result,
  clubId,
  meetId,
  navigate,
  dispatch
) => {
  const response = await getEvents(clubId, meetId);
  const events = response.data;

  const event = events.find((event) => {
    const rounds = event.rounds;
    const round = rounds.find(
      (round) =>
        round.eventID === result.eventID || round.eventID === result.eventId
    );

    return !!round;
  });

  console.log('[navigateToEventView] event', event);
  dispatch(updateSelectedAgeResult(null));
  dispatch(updateSelectedEvent(event));
  navigate(
    routes.admin.results.url(
      clubId,
      meetId,
      'event-view',
      event.eventType,
      event.ageGroup,
      event.gender
    )
  );
};

export const navigateToEventViewFromAgeView = (
  result,
  clubId,
  meetId,
  eventType,
  navigate
) => {
  const eventId = result.eventID || result.eventId;
  navigate(routes.admin.eventRound.url(clubId, meetId, eventType, eventId));
};

export const navigateToEventViewForClubRecord = async (
  clubId,
  clubRecord,
  navigate,
  dispatch
) => {
  const response = await getEvents(clubId, clubRecord.meetId);
  const events = response.data;
  const event = events.find((event) => {
    const rounds = event.rounds;
    const round = rounds.find((round) => round.eventID === clubRecord.eventId);

    return !!round;
  });
  if (event) {
    navigate(
      routes.admin.eventRound.url(
        clubId,
        clubRecord.meetId,
        event.eventType,
        clubRecord.eventId
      )
    );
  } else {
    throw Error(`cannot find event for the club records`);
  }
};

export const resultsToHighJumpTableData = (results, eventType) => {
  const heightsArray = getResultHighJumpHeights(results);
  const data = [];
  results.forEach((result) => {
    let highJumpAttempts = [];
    for (let i = 0; i < heightsArray.length; i++) {
      const attempt = result?.heightAttempts.find(
        (ha) => ha.height === heightsArray[i]
      );
      if (attempt) {
        highJumpAttempts.push(
          <div
            style={{ paddingLeft: 20, minWidth: 70, paddingRight: 10 }}
            key={i}
          >
            {`${attempt.attempt1} ${attempt.attempt2} ${attempt.attempt3}`}
          </div>
        );
      } else {
        highJumpAttempts.push(<div key={i}></div>);
      }
    }
    let res = {
      ...result,
      athleteNumber: result.athleteNumber || 'Guest',
      age: result.age && result.age > 0 ? result.age : '',
      result:
        result.bestClearedHeight && result.bestClearedHeight > 0
          ? `${Number(result.bestClearedHeight).toFixed(2)}`
          : 'NR',
      seasonBestValue:
        result.seasonBestValue && result.seasonBestValue > 0
          ? `${Number(result.seasonBestValue).toFixed(2)}`
          : '',
      date: `${convertDateTime(result.timestamp || '').date} ${convertDateTime(result.timestamp || '').time}`,
      athleteName: `${result.firstName} ${result.lastName}`,
      position: result.position || '',
      pr: result.isPR ? 'YES' : 'NO',
      highJumpAttempts,
      eventType,
      resultID: result.athleteId,
    };
    for (let i = 0; i < heightsArray.length; i++) {
      const attempt = result?.heightAttempts.find(
        (ha) => ha.height === heightsArray[i]
      );
      if (attempt) {
        res[`heightAttempts${i}`] = attempt;
      } else {
        res[`heightAttempts${i}`] = { height: heightsArray[i], empty: true };
      }
    }
    data.push(res);
  });
  return data;
};

/**
 * Converts an array of high jump result objects into a format suitable for displaying
 * in an age view high jump event table, including athlete details and action buttons.
 *
 * @param {Array} results - The list of high jump result objects to transform.
 * @param {string|number} clubId - The identifier for the club.
 * @param {string|number} meetId - The identifier for the meet.
 * @param {string} resultType - The type of result (e.g., high jump).
 * @param {NavigateFunction} navigate - Navigation function to handle routing to event views.
 * @param {DispatchType} dispatch - Dispatch function for state management or side effects.
 * @returns {Array} An array of transformed result objects formatted for table display.
 */
export const resultsToAgeViewHighJumpTableData = (
  results,
  clubId,
  meetId,
  eventType,
  roundType,
  navigate,
  dispatch
) => {
  const data = [];
  results.forEach((result) => {
    data.push({
      ...result,
      result:
        result.bestClearedHeight && result.bestClearedHeight > 0
          ? `${Number(result.bestClearedHeight).toFixed(2)}`
          : 'NR',
      seasonBestValue:
        result.seasonBestValue && result.seasonBestValue > 0
          ? `${Number(result.seasonBestValue).toFixed(2)}`
          : '',
      date: `${convertDateTime(result.timestamp || '').date} ${convertDateTime(result.timestamp || '').time}`,
      athleteName: `${result.firstName} ${result.lastName}`,
      position: result.position || '',
      pr: result.isPR ? 'YES' : 'NO',
      resultID: result.athleteId,
      button: (
        <IconTextButton
          text=""
          icon={<EyeIcon />}
          onClick={() =>
            navigateToEventViewFromAgeView(
              result,
              clubId,
              meetId,
              eventType,
              navigate
            )
          }
          style={{ marginRight: 10 }}
          textColor={redColor}
          borderColor={whiteColor}
          height="unset"
          iconPadding="5px"
          iconColor={redColor}
          iconPosition="right"
        />
      ),
    });
  });
  return data;
};
